import React, { useCallback, useEffect, useState } from "react";
import { fireGameStagePopup } from "../../functions/gameStagePopup";
import { isMobViewport } from "./gameStageBackground";
import {
  PRINCIPLE_HOTSPOTS,
  PRINCIPLE_WORDS,
  STRESS_AFTER_CHANNEL,
  STRESS_BALANCED,
  STAGE2_PRINCIPLES_PUZZLE_POINTS,
  stressAfterFinds,
} from "./stage2PrinciplesData";

function buildPrinciplesProgressHtml(foundWords) {
  const found = PRINCIPLE_WORDS.filter((word) => foundWords.includes(word));
  const remaining = PRINCIPLE_WORDS.filter((word) => !foundWords.includes(word));

  const renderList = (words, variant) =>
    words
      .map(
        (word) =>
          `<li class="game-stage-popup__word-item game-stage-popup__word-item--${variant}">${word}</li>`
      )
      .join("");

  return `<div class="game-stage-popup__word-progress">
    <div class="game-stage-popup__word-col">
      <span class="game-stage-popup__word-heading">Found</span>
      <ul class="game-stage-popup__word-list">${renderList(found, "found")}</ul>
    </div>
    <div class="game-stage-popup__word-col">
      <span class="game-stage-popup__word-heading">Remaining</span>
      <ul class="game-stage-popup__word-list">${
        remaining.length
          ? renderList(remaining, "remaining")
          : '<li class="game-stage-popup__word-item game-stage-popup__word-item--done">All found!</li>'
      }</ul>
    </div>
  </div>`;
}

function PrinciplesChecklist({ found }) {
  return (
    <aside className="stage2-principles__checklist" aria-label="Principles progress">
      <p className="stage2-principles__checklist-title">Find these words</p>
      <ul className="stage2-principles__checklist-list">
        {PRINCIPLE_WORDS.map((word) => {
          const isFound = found.includes(word);
          return (
            <li
              key={word}
              className={`stage2-principles__checklist-item${
                isFound ? " stage2-principles__checklist-item--found" : ""
              }`}
            >
              <span className="stage2-principles__checklist-mark" aria-hidden>
                {isFound ? "✓" : "○"}
              </span>
              <span className="stage2-principles__checklist-word">{word}</span>
            </li>
          );
        })}
      </ul>
      <p className="stage2-principles__checklist-count">
        {found.length} / {PRINCIPLE_WORDS.length} found
      </p>
    </aside>
  );
}

function StressMeter({ value, variant = "vertical" }) {
  const isHorizontal = variant === "horizontal";
  const isBalanced = value <= STRESS_BALANCED + 4;

  return (
    <div
      className={`stage1-stress stage1-stress--${variant} stage2-principles__stress${
        isBalanced ? " stage1-stress--balanced" : ""
      }`}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Stress meter ${value} percent`}
    >
      <span className="stage1-stress__heading">Stress Meter</span>
      <div className="stage1-stress__track">
        <div
          className="stage1-stress__fill"
          style={
            isHorizontal ? { width: `${value}%` } : { height: `${value}%` }
          }
        />
      </div>
      <span className="stage1-stress__value">
        {value <= STRESS_BALANCED + 4 ? "Balanced" : `${value}%`}
      </span>
    </div>
  );
}

export default function Stage2HiddenPrinciples({
  onComplete,
  stress = STRESS_AFTER_CHANNEL,
  onStressChange,
  showInternalStress = true,
  submitting = false,
  themeData,
}) {
  const [found, setFound] = useState([]);
  const [pulseId, setPulseId] = useState(null);
  const [mob, setMob] = useState(() => isMobViewport());

  useEffect(() => {
    const update = () => setMob(isMobViewport());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  const handleHotspot = useCallback(
    async (word) => {
      if (found.includes(word) || submitting) return;

      const nextFound = [...found, word];
      const isComplete = nextFound.length === PRINCIPLE_WORDS.length;
      const newStress = isComplete
        ? STRESS_BALANCED
        : stressAfterFinds(nextFound.length, STRESS_AFTER_CHANNEL);

      setFound(nextFound);
      onStressChange?.(newStress);
      setPulseId(word);
      window.setTimeout(() => setPulseId((current) => (current === word ? null : current)), 900);

      const progressHtml = buildPrinciplesProgressHtml(nextFound);

      await fireGameStagePopup({
        title: isComplete ? "All Principles Found!" : "Principle Found!",
        html: isComplete
          ? `<p>You discovered every productivity principle hidden in the room. Your stress meter is balanced.</p>${progressHtml}<p><strong>+${STAGE2_PRINCIPLES_PUZZLE_POINTS} points</strong></p>`
          : `<p>You discovered <strong>${word}</strong>.</p>${progressHtml}`,
        icon: "success",
        confirmButtonText: "OK",
        themeData,
      });

      if (isComplete) {
        await onComplete?.({ found: nextFound });
      }
    },
    [found, submitting, themeData, onComplete, onStressChange]
  );

  const stressMeter = (
    <StressMeter value={stress} variant={mob ? "horizontal" : "vertical"} />
  );

  return (
    <div
      className={`stage2-principles${
        mob ? " stage2-principles--mob" : ""
      }${!showInternalStress ? " stage2-principles--external-stress" : ""}`}
    >
      {showInternalStress ? (
        mob ? (
          <div className="stage2-principles__stress-bar">{stressMeter}</div>
        ) : (
          stressMeter
        )
      ) : null}

      <PrinciplesChecklist found={found} />

      <div className="stage2-principles__scene">
        {PRINCIPLE_HOTSPOTS.map((spot) => {
          const isFound = found.includes(spot.id);
          const isPulsing = pulseId === spot.id;

          if (isFound) {
            return (
              <div
                key={spot.id}
                data-principle={spot.id}
                className={`stage2-principles__found-marker${
                  isPulsing ? " stage2-principles__found-marker--pop" : ""
                }`}
                aria-label={`Found principle: ${spot.label}`}
              >
                <span className="stage2-principles__found-ring" aria-hidden />
                <span
                  className="stage2-principles__found-ring stage2-principles__found-ring--delay"
                  aria-hidden
                />
                <span className="stage2-principles__found-label">{spot.label}</span>
              </div>
            );
          }

          return (
            <button
              key={spot.id}
              type="button"
              data-principle={spot.id}
              className="stage2-principles__hotspot"
              aria-label={`Find hidden principle: ${spot.label}`}
              disabled={submitting}
              onClick={() => handleHotspot(spot.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
